<template>
  <div class="min-h-screen flex-1 mt-1 bg-gray-100 px-4 pt-12 shadow-lg items-center">
    <div class="bg-white w-full rounded-lg shadow">
      <div class="h-20 py-3 flex items-center border-gray-200 p-6 mt-2">
        <div class="flex">
          <router-link
            :class="$colors.primary"
            class="rounded-full flex justify-center items-center w-8 h-8 mt-1 mr-3"
            to="/seller-team"
          >
            <i class="material-icons mx-2">chevron_left</i>
          </router-link>
          <div v-if="!formData._id" class="text-2xl font-bold text-green-500">
            {{ $t('add_team_member') || 'Add Team Member' }}
          </div>
          <div v-else class="text-2xl font-bold text-green-500">
            {{ $t('edit_team_member') || 'Edit Team Member' }}
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg p-6 pt-2">
        <!-- Basic Info Section -->
        <div class="mb-6">
          <h3 class="text-lg font-medium text-gray-700 mb-4">{{ $t('basic_info') || 'Basic Information' }}</h3>
          <div class="grid lg:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div class="border hover:border-green-500 focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
              <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                <p>
                  <label for="fullName" class="bg-white text-gray-700 px-1">
                    {{ $t('full_name') || 'Full Name' }} <span class="text-red-500">*</span>
                  </label>
                </p>
              </div>
              <p>
                <input
                  id="fullName"
                  type="text"
                  v-model="formData.fullName"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  :placeholder="$t('enter_full_name') || 'Enter full name'"
                />
              </p>
            </div>

            <!-- Email (only for new members) -->
            <div v-if="!formData._id" class="border hover:border-green-500 focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
              <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                <p>
                  <label for="email" class="bg-white text-gray-700 px-1">
                    {{ $t('email') || 'Email' }} <span class="text-red-500">*</span>
                  </label>
                </p>
              </div>
              <p>
                <input
                  id="email"
                  type="email"
                  v-model="formData.email"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  :placeholder="$t('enter_email') || 'Enter email address'"
                />
              </p>
            </div>

            <!-- Phone -->
            <div class="border hover:border-green-500 focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
              <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                <p>
                  <label for="phone" class="bg-white text-gray-700 px-1">
                    {{ $t('phone') || 'Phone' }} <span class="text-red-500">*</span>
                  </label>
                </p>
              </div>
              <p>
                <input
                  id="phone"
                  type="tel"
                  v-model="formData.phone"
                  class="py-2 text-xs px-2 outline-none block h-full w-full"
                  :placeholder="$t('enter_phone') || 'Enter phone number'"
                />
              </p>
            </div>

            <!-- Team Role -->
            <div class="border hover:border-green-500 focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
              <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                <p>
                  <label for="teamRole" class="bg-white text-gray-700 px-1">
                    {{ $t('team_role') || 'Team Role' }} <span class="text-red-500">*</span>
                  </label>
                </p>
              </div>
              <p>
                <select
                  id="teamRole"
                  v-model="formData.teamRole"
                  @change="applyRoleDefaults"
                  class="py-2 text-xs px-2 outline-none block h-full w-full bg-white"
                >
                  <option value="manager">{{ $t('manager') || 'Manager' }} - {{ $t('full_access_except_delete') || 'Full access except delete' }}</option>
                  <option value="operator">{{ $t('operator') || 'Operator' }} - {{ $t('create_edit_orders') || 'Create & edit orders' }}</option>
                  <option value="viewer">{{ $t('viewer') || 'Viewer' }} - {{ $t('read_only_access') || 'Read-only access' }}</option>
                </select>
              </p>
            </div>

            <!-- Status (only for editing) -->
            <div v-if="formData._id" class="border hover:border-green-500 focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
              <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
                <p>
                  <label for="status" class="bg-white text-gray-700 px-1">
                    {{ $t('status') || 'Status' }}
                  </label>
                </p>
              </div>
              <p>
                <select
                  id="status"
                  v-model="formData.status"
                  class="py-2 text-xs px-2 outline-none block h-full w-full bg-white"
                >
                  <option value="active">{{ $t('active') || 'Active' }}</option>
                  <option value="inactive">{{ $t('inactive') || 'Inactive' }}</option>
                  <option value="blocked">{{ $t('blocked') || 'Blocked' }}</option>
                </select>
              </p>
            </div>
          </div>
        </div>

        <!-- Permissions Section -->
        <div class="border hover:border-green-500 mt-4 px-4 w-full focus-within:border-blue-500 focus-within:text-green-500 transition-all duration-500 relative rounded p-1">
          <div class="-mt-4 absolute tracking-wider px-1 capitalize text-xs">
            <p>
              <label class="bg-white text-gray-700 px-1">
                {{ $t('permissions') || 'Permissions' }} <span class="text-red-500">*</span>
              </label>
            </p>
          </div>

          <div class="mt-4 mb-2">
            <!-- Loading state -->
            <div v-if="loadingPermissions" class="text-center py-4">
              <i class="material-icons animate-spin text-green-500">refresh</i>
              <p class="text-sm text-gray-500">{{ $t('loading_permissions') || 'Loading permissions...' }}</p>
            </div>

            <!-- Permissions table -->
            <div v-else class="w-full">
              <table class="table w-full text-center">
                <thead class="bg-gray-100 text-black">
                  <tr>
                    <th class="px-5 py-2 border-gray-200 text-left text-xs font-semibold text-gray-700 capitalize tracking-wider">
                      {{ $t('module') || 'Module' }}
                    </th>
                    <th class="px-5 py-2 border-gray-200 text-left text-xs font-semibold text-gray-700 capitalize tracking-wider">
                      {{ $t('view') || 'View' }}
                    </th>
                    <th class="px-5 py-2 border-gray-200 text-left text-xs font-semibold text-gray-700 capitalize tracking-wider">
                      {{ $t('create') || 'Create' }}
                    </th>
                    <th class="px-5 py-2 border-gray-200 text-left text-xs font-semibold text-gray-700 capitalize tracking-wider">
                      {{ $t('edit') || 'Edit' }}
                    </th>
                    <th class="px-5 py-2 border-gray-200 text-left text-xs font-semibold text-gray-700 capitalize tracking-wider">
                      {{ $t('delete') || 'Delete' }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <!-- Dashboard -->
                  <tr v-if="sellerPermissions.dashboard && hasAnyAccess(sellerPermissions.dashboard)" class="bg-white">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('dashboard') || 'Dashboard' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.dashboard.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewDashboard" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- Products -->
                  <tr v-if="sellerPermissions.products && hasAnyAccess(sellerPermissions.products)" class="bg-gray-50">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('products') || 'Products' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.products.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewProducts" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.products.create" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canCreateProducts" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.products.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canEditProducts" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.products.delete" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canDeleteProducts" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                  </tr>

                  <!-- Orders -->
                  <tr v-if="sellerPermissions.orders && hasAnyAccess(sellerPermissions.orders)" class="bg-white">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('orders') || 'Orders' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.orders.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewOrders" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.orders.create" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canCreateOrders" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.orders.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canEditOrders" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.orders.delete" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canDeleteOrders" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                  </tr>

                  <!-- Expeditions -->
                  <tr v-if="sellerPermissions.expeditions && hasAnyAccess(sellerPermissions.expeditions)" class="bg-gray-50">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('expeditions') || 'Expeditions' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.expeditions.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewExpeditions" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.expeditions.create" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canCreateExpeditions" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.expeditions.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canEditExpeditions" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- Shippings -->
                  <tr v-if="sellerPermissions.shippings && hasAnyAccess(sellerPermissions.shippings)" class="bg-white">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('shipping_order') || 'Shipping Order' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.shippings.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewShippings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.shippings.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canManageShippings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- Payments -->
                  <tr v-if="sellerPermissions.payments && hasAnyAccess(sellerPermissions.payments)" class="bg-gray-50">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('payments') || 'Payments' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.payments.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewPayments" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- Sourcings -->
                  <tr v-if="sellerPermissions.sourcings && hasAnyAccess(sellerPermissions.sourcings)" class="bg-white">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('sourcings') || 'Sourcings' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.sourcings.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewSourcings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.sourcings.create" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canCreateSourcings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.sourcings.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canEditSourcings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- My Team -->
                  <tr v-if="sellerPermissions.sellerTeam && hasAnyAccess(sellerPermissions.sellerTeam)" class="bg-gray-50">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('my_team') || 'My Team' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canManageTeam" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- Settings -->
                  <tr v-if="sellerPermissions.settings && hasAnyAccess(sellerPermissions.settings)" class="bg-white">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('settings') || 'Settings' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.settings.read" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewSettings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label v-if="sellerPermissions.settings.update" class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canEditSettings" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                      <span v-else class="text-gray-300">-</span>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- API -->
                  <tr class="bg-gray-50">
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <p class="text-gray-700 capitalize font-medium">{{ $t('API') || 'API' }}</p>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm">
                      <label class="inline-flex text-sm items-center">
                        <input type="checkbox" v-model="formData.teamPermissions.canViewApi" class="form-checkbox border-gray-500 h-4 w-4 text-green-500" />
                      </label>
                    </td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                    <td class="px-5 py-2 pt-3 text-left border-gray-200 text-sm"><span class="text-gray-300">-</span></td>
                  </tr>

                  <!-- No permissions available -->
                  <tr v-if="!hasAnySellerPermission">
                    <td colspan="5" class="px-5 py-8 text-center text-gray-500">
                      <i class="material-icons text-4xl text-gray-300 mb-2">lock</i>
                      <p>{{ $t('no_permissions_configured') || 'No permissions configured for this seller role' }}</p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Save Button -->
        <div class="mt-6 pt-3 flex justify-center">
          <button
            @click="save"
            :disabled="loading"
            class="rounded focus:outline-none outline-none text-gray-100 px-3 w-1/6 py-2 bg-green-600 hover:shadow-inner hover:bg-green-700 transition-all duration-300 disabled:opacity-50"
          >
            <span v-if="loading">{{ $t('saving') || 'Saving...' }}</span>
            <span v-else>{{ $t('save') || 'Save' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SellerTeamForm',
  props: {
    currentUser: { type: Object },
    idWharhouse: { type: Object },
  },
  data() {
    return {
      formData: {
        fullName: '',
        email: '',
        phone: '',
        teamRole: 'operator',
        status: 'active',
        teamPermissions: {
          canViewOrders: true,
          canCreateOrders: false,
          canEditOrders: false,
          canDeleteOrders: false,
          canViewProducts: true,
          canCreateProducts: false,
          canEditProducts: false,
          canDeleteProducts: false,
          canViewShippings: true,
          canManageShippings: false,
          canViewExpeditions: true,
          canCreateExpeditions: false,
          canEditExpeditions: false,
          canViewPayments: true,
          canViewAnalytics: false,
          canViewSourcings: true,
          canCreateSourcings: false,
          canEditSourcings: false,
          canManageTeam: false
        }
      },
      sellerPermissions: {},
      loading: false,
      loadingPermissions: true
    };
  },
  computed: {
    hasAnySellerPermission() {
      const modules = ['orders', 'products', 'shippings', 'expeditions', 'payments', 'analytics', 'dashboard', 'sourcings', 'sellerTeam'];
      for (let module of modules) {
        if (this.sellerPermissions[module] && this.hasAnyAccess(this.sellerPermissions[module])) {
          return true;
        }
      }
      return false;
    }
  },
  async mounted() {
    await this.loadSellerPermissions();
    if (this.$route.params.id) {
      await this.loadMember();
    } else {
      this.applyRoleDefaults();
    }
  },
  methods: {
    async loadSellerPermissions() {
      this.loadingPermissions = true;
      try {
        // Get permissions from currentUser's role
        if (this.currentUser && this.currentUser.role && this.currentUser.role.permissions) {
          const permissions = this.currentUser.role.permissions;

          // Convert array of permissions to object for easy access
          for (let perm of permissions) {
            this.sellerPermissions[perm.model] = perm.access;
          }
        }
      } catch (error) {
        console.error('Error loading seller permissions:', error);
      } finally {
        this.loadingPermissions = false;
      }
    },
    async loadMember() {
      try {
        const res = await this.$server.getTeamMember('sellerTeam', { _id: this.$route.params.id });
        if (res && res.content) {
          this.formData = {
            _id: res.content._id,
            fullName: res.content.fullName,
            email: res.content.email,
            phone: res.content.phone,
            teamRole: res.content.teamRole,
            status: res.content.status,
            teamPermissions: res.content.teamPermissions || this.getDefaultPermissions('operator')
          };
        }
      } catch (error) {
        console.error('Error loading member:', error);
      }
    },
    hasAnyAccess(access) {
      if (!access) return false;
      return access.create || access.read || access.update || access.delete;
    },
    applyRoleDefaults() {
      const defaults = this.getDefaultPermissions(this.formData.teamRole);
      // Only apply defaults for permissions the seller actually has
      for (let key in defaults) {
        // Check if seller has this permission before applying default
        this.formData.teamPermissions[key] = defaults[key];
      }
    },
    getDefaultPermissions(role) {
      const permissions = {
        manager: {
          canViewOrders: true,
          canCreateOrders: true,
          canEditOrders: true,
          canDeleteOrders: false,
          canViewProducts: true,
          canCreateProducts: true,
          canEditProducts: true,
          canDeleteProducts: false,
          canViewShippings: true,
          canManageShippings: true,
          canViewExpeditions: true,
          canCreateExpeditions: true,
          canEditExpeditions: true,
          canViewPayments: true,
          canViewAnalytics: true,
          canViewSourcings: true,
          canCreateSourcings: true,
          canEditSourcings: true,
          canManageTeam: true
        },
        operator: {
          canViewOrders: true,
          canCreateOrders: true,
          canEditOrders: true,
          canDeleteOrders: false,
          canViewProducts: true,
          canCreateProducts: false,
          canEditProducts: false,
          canDeleteProducts: false,
          canViewShippings: true,
          canManageShippings: false,
          canViewExpeditions: true,
          canCreateExpeditions: false,
          canEditExpeditions: false,
          canViewPayments: true,
          canViewAnalytics: false,
          canViewSourcings: true,
          canCreateSourcings: false,
          canEditSourcings: false,
          canManageTeam: false
        },
        viewer: {
          canViewOrders: true,
          canCreateOrders: false,
          canEditOrders: false,
          canDeleteOrders: false,
          canViewProducts: true,
          canCreateProducts: false,
          canEditProducts: false,
          canDeleteProducts: false,
          canViewShippings: true,
          canManageShippings: false,
          canViewExpeditions: true,
          canCreateExpeditions: false,
          canEditExpeditions: false,
          canViewPayments: true,
          canViewAnalytics: false,
          canViewSourcings: true,
          canCreateSourcings: false,
          canEditSourcings: false,
          canManageTeam: false
        }
      };
      return permissions[role] || permissions.viewer;
    },
    checkData() {
      if (!this.formData.fullName) {
        alert(this.$t('full_name_required') || 'Full name is required', 'warning');
        return false;
      }
      if (!this.formData._id && !this.formData.email) {
        alert(this.$t('email_required') || 'Email is required', 'warning');
        return false;
      }
      if (!this.formData.phone) {
        alert(this.$t('phone_required') || 'Phone is required', 'warning');
        return false;
      }
      return true;
    },
    async save() {
      if (!this.checkData()) return;

      this.loading = true;
      try {
        let res;
        if (this.formData._id) {
          res = await this.$server.updateTeamMember('sellerTeam', this.formData);
        } else {
          res = await this.$server.addTeamMember('sellerTeam', this.formData);
        }

        if (res && res.content) {
          alert(this.formData._id ? (this.$t('member_updated') || 'Member updated successfully') : (this.$t('member_added') || 'Member added successfully'), 'success');
          this.$router.push('/seller-team');
        }
      } catch (error) {
        console.error('Error saving member:', error);
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
